import { Box } from '@mui/material';
import { Location } from 'history';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import {
    ABTestType,
    AudienceType,
    CampaignChannels,
    DirectMailSendInput,
    DirectMailSendInputState,
    EmailSendInput,
    EmailSendInputState,
    ExitActionInput,
    SmsSendInput,
    SmsSendInputState,
    SplitActions,
    SplitTypes,
    VoiceCallSendInput,
} from 'domains/campaigns/types';
import ContentUnavailableAlert from 'domains/core/components/ContentUnavailableAlert';
import Flow from 'domains/core/components/Flow';
import RouteLeavingGuard from 'domains/core/components/RouteLeavingGuard';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessDialog from 'domains/core/components/SuccessDialog';
import CampaignStatusText from 'domains/campaigns/pages/CurationsPage/CampaignsTable/CampaignStatusText';
import { recordSatismeterEvent } from 'helpers/injectSatismeter/injectSatismeter';
import { trackCuredEvent, MixpanelEvent, MixpanelFlow, CampaignFlowType } from 'helpers/initMixpanel';
import useCreateCampaign from 'hooks/mutations/useCreateCampaign';
import useCreateDraftCampaign from 'hooks/mutations/useCreateDraftCampaign';
import useUpdateAndResumeCampaign from 'hooks/mutations/useUpdateAndResumeCampaign';
import useUpdateDraftCampaign from 'hooks/mutations/useUpdateDraftCampaign';
import QueryKeys from 'hooks/queries/keys';
import { CampaignStatus, URLPaths } from 'models/enums';
import { removeSend, setName } from './customCampaignActions';
import CustomCampaignContentPreview from './CustomCampaignContentPreview';
import CustomCampaignForm from './CustomCampaignForm';
import CustomCampaignNameInput from './CustomCampaignNameInput';
import CustomCampaignNavigation from './CustomCampaignNavigation';
import GraphCampaignNavigation from './GraphCampaignNavigation';
import CustomCampaignNavigationButtons from './CustomCampaignNavigationButtons';
import {
    CampaignStateTypes,
    emailInitialState,
    directMailInitialState,
    smsInitialState,
    voiceCallInitialState,
} from './customCampaignReducer';
import { getEffectiveSplitType, getSegmentsForApi } from './utils';
import {
    campaignDialogProps,
    draftCampaignDialogProps,
} from 'domains/core/components/RouteLeavingGuard/RouteLeavingGuard';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useQueryParams from 'hooks/useQueryParams';

const DraftSuccessBody =
    "Nice work, you've successfully save your Curation as draft. Continue editing the curation or exit and return to it later.";
const PublishSuccessBody =
    "Nice work, you've successfully scheduled your Curation. Once it sends, visit Insights to monitor its performance.";

export enum CustomCampaignSteps {
    NAME_CAMPAIGN = 'name-campaign',
    SELECT_AUDIENCE = 'select-audience',
    SELECT_CONTENT = 'select-content',
    CHOOSE_NEXT_STEP = 'choose-next-step',
    CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT = 'choose-next-step-conditional-split',
    SET_TIME_DELAY = 'set-time-delay',
    CHOOSE_SEND_TYPE = 'choose-send-type',
    CONDITIONAL_SPLIT = 'conditional-split',
    SELECT_PATH_ACTION = 'select-path-action',
    SCHEDULE_CURATION = 'schedule-curation',
    PUBLISH_CAMPAIGN = 'publish-campaign',
    SETUP_A_B_TEST = 'setup-a-b-test',
}

const CustomCampaign = ({
    campaignState,
    dispatchCampaignAction,
    campaignId,
}: {
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    campaignId?: string;
}) => {
    // Navigation State Values
    const [activeStep, setActiveStep] = useState(CustomCampaignSteps.NAME_CAMPAIGN);
    const [nextStep, setNextStep] = useState(CustomCampaignSteps.SELECT_AUDIENCE);
    const [activeSendIndex, setActiveSendIndex] = useState(0);

    const [contentPreviewId, setContentPreviewId] = useState(null);
    const [customContinueHandler, setCustomContinueHandler] = useState<(() => void) | null>(null);
    const [initialContentSubStep, setInitialContentSubStep] = useState<'selecting' | 'scheduling' | undefined>(
        undefined
    );

    const {
        curationsDraft: hasCurationsDraft,
        editLiveCurationsFeature: hasEditLiveCurationsFeatureFlag,
        multipleSendsConditionalSplit: hasMultipleSendsConditionalSplitFlag,
        campaignGraphNavigation: hasCampaignGraphNavigationFlag,
    } = useFlags();
    // Custom continue handler setter with React functional update fix
    const setCustomContinueHandlerFixed = (handler: (() => void) | null) => {
        if (handler) {
            // FIX: Use functional update pattern to prevent React from calling the handler immediately
            // React treats functions passed to setState as potential functional updates
            // By wrapping in () => handler, React calls the wrapper and gets the actual handler
            setCustomContinueHandler(() => handler);
        } else {
            setCustomContinueHandler(null);
        }
    };

    const [isContinueEnabled, setIsContinueEnabled] = useState(false);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [isSaveDraftClicked, setIsSaveDraftClicked] = useState(false);
    const [savedDraftCampaignId, setSavedDraftCampaignId] = useState<string | null>(null);
    // Track if content is unavailable (unpublished/deleted) for showing page-level alert
    const [isContentUnavailable, setIsContentUnavailable] = useState(false);
    const [unavailableContentId, setUnavailableContentId] = useState<number | null>(null);

    const query = useQueryParams();
    const statusParam = query.get('status');
    const status = statusParam ? (statusParam as CampaignStatus) : null;
    const disableNameEditing = hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;
    const isEditingPausedCuration = hasEditLiveCurationsFeatureFlag && status === CampaignStatus.PAUSED;

    // Strip non-content fields from NOOP sends when editing paused curations
    // Only keep minimal identifying fields to avoid API validation issues
    const sanitizeSendForPausedEdit = (send: any) => {
        if (send.channel === CampaignChannels.NOOP) {
            return {
                channel: CampaignChannels.NOOP,
                parentIndex: send.parentIndex,
                splitAction: send.splitAction,
                splitType: send.splitType,
            };
        }
        return send;
    };

    // Handle content unavailable state change
    const handleContentUnavailableChange = (isUnavailable: boolean, contentId?: number | null) => {
        setIsContentUnavailable(isUnavailable);
        setUnavailableContentId(contentId ?? null);
    };

    // Helper function to navigate to A/B test scheduling for specific email
    const navigateToABTestScheduling = (emailIndex: number) => {
        setActiveSendIndex(emailIndex);
        setInitialContentSubStep('scheduling');
        setActiveStep(CustomCampaignSteps.SELECT_CONTENT);
    };

    // Reset initialContentSubStep when leaving the content step
    useEffect(() => {
        if (activeStep !== CustomCampaignSteps.SELECT_CONTENT) {
            setInitialContentSubStep(undefined);
        }
    }, [activeStep]);

    const history = useHistory();
    const queryClient = useQueryClient();

    const {
        mutate,
        isError: isCreateCampaignError,
        isLoading: isCreateCampaignLoading,
        error: createCampaignError,
    } = useCreateCampaign(campaignId, {
        onSuccess: (data) => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            setIsSuccessDialogOpen(true);

            // Track campaign published
            const firstSend = campaignState.sends?.[0];
            trackCuredEvent(
                MixpanelEvent.CAMPAIGN_PUBLISHED,
                {
                    campaign_id: data?.id || campaignId,
                    campaign_type: firstSend?.channel || 'unknown',
                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                    campaign_name: campaignState.name,
                },
                MixpanelFlow.CAMPAIGN
            );
        },
    });

    // Separate mutation for publish and resume (calls PUT /campaigns/:id?resume=true)
    const {
        mutate: mutatePublishAndResume,
        isError: isPublishAndResumeError,
        isLoading: isPublishAndResumeLoading,
        error: publishAndResumeError,
    } = useUpdateAndResumeCampaign(campaignId || '', {
        onSuccess: () => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            setIsSuccessDialogOpen(true);
        },
    });

    const {
        mutate: mutateCreateDraft,
        isError: isCreateDraftCampaignError,
        isLoading: isCreateDraftCampaignLoading,
        error: createDraftCampaignError,
    } = useCreateDraftCampaign({
        onSuccess: (data) => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            // Store the campaign ID for navigation
            setSavedDraftCampaignId(data.id);
            setIsSuccessDialogOpen(true);

            // Track draft campaign created
            const firstSend = campaignState.sends?.[0];
            trackCuredEvent(
                MixpanelEvent.DRAFT_CAMPAIGN_SAVED,
                {
                    campaign_id: data?.id,
                    campaign_type: firstSend?.channel || 'unknown',
                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                    campaign_name: campaignState.name,
                    is_new_draft: true,
                },
                MixpanelFlow.CAMPAIGN
            );
        },
    });

    const {
        mutate: mutateUpdateDraft,
        isError: isUpdateDraftCampaignError,
        isLoading: isUpdateDraftCampaignLoading,
        error: updateDraftCampaignError,
    } = useUpdateDraftCampaign(campaignId || '', {
        onSuccess: () => {
            // Invalidate queries used for the sends and campaigns tables
            queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
            // For update, use the existing campaignId
            setSavedDraftCampaignId(campaignId || null);
            setIsSuccessDialogOpen(true);

            // Track draft campaign updated
            const firstSend = campaignState.sends?.[0];
            trackCuredEvent(
                MixpanelEvent.DRAFT_CAMPAIGN_SAVED,
                {
                    campaign_id: campaignId,
                    campaign_type: firstSend?.channel || 'unknown',
                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                    campaign_name: campaignState.name,
                    is_new_draft: false,
                },
                MixpanelFlow.CAMPAIGN
            );
        },
    });

    // Closing content preview when switching steps
    useEffect(() => {
        setContentPreviewId(null);
    }, [activeStep]);

    const getEmailSendForApi = (
        send: EmailSendInputState,
        index: number,
        campaignState: CampaignStateTypes
    ): EmailSendInput | ExitActionInput => {
        if (send?.splitAction === SplitActions.EXIT) {
            const { parentIndex, splitAction, splitType, parentAudienceDelayInterval, segmentId } = send;

            const exitActionInput: ExitActionInput = {
                channel: CampaignChannels.NOOP,
                parentIndex,
                splitAction,
                splitType,
                ...(parentAudienceDelayInterval && { parentAudienceDelayInterval }),
                ...(segmentId && { segmentId }),
            };

            if (send.segments && send.segments.length > 0) {
                getSegmentsForApi(send, exitActionInput as any);
            }

            return exitActionInput;
        }

        let sendForApi: EmailSendInput = {
            channel: send.channel || CampaignChannels.EMAIL,
            name: send.name,
            preHeader: send.preHeader,
            senderProfileId: send.senderProfileId,
            subjectLine: send.subjectLine,
            templateId: send.templateId,
            segmentId: send.segmentId || null, // Always include segmentId, even if null
        };

        // A/B Testing: Both Email-A (index 0) and Email-B (index 1) should have scheduledTimestamp
        const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
        const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;
        const isABTesting = isABTestingContent || isABTestingSendTime;
        const shouldIncludeSchedule = index === 0 || (isABTesting && index === 1);

        if (shouldIncludeSchedule) {
            // For A/B testing, Email-B should inherit segmentId and hasRecommendedTime from Email-A
            const isEmailB = isABTesting && index === 1;
            const emailASend = isEmailB ? (campaignState.sends[0] as EmailSendInputState) : null;

            // Determine which values to use for segmentId and hasRecommendedTime
            const segmentIdToUse = isEmailB ? emailASend?.segmentId : send.segmentId;
            const hasRecommendedTimeToUse = isEmailB ? emailASend?.hasRecommendedTime : send.hasRecommendedTime;

            return {
                ...sendForApi,
                recurrenceFrequency: send.recurrenceFrequency,
                scheduledTimestamp: send.scheduledTimestamp,
                segmentId: segmentIdToUse || null, // Always include segmentId, even if null
                ...(send.cohortId && { cohortId: send.cohortId }),
                ...(send.scheduledEndTimestamp && { scheduledEndTimestamp: send.scheduledEndTimestamp }),
                ...(hasRecommendedTimeToUse !== undefined && { hasRecommendedTime: hasRecommendedTimeToUse }),
            };
        }

        if (!!send.segments) {
            getSegmentsForApi(send, sendForApi);
        }

        if (!!send.splitAction && !!send.splitType) {
            const { splitAction, splitType } = send;

            // Only include splitAction and splitType for the first send of each flow
            // Check if parent has the same splitType - if yes, this is a descendant
            const parentSend =
                send.parentIndex !== undefined && send.parentIndex !== null
                    ? campaignState.sends[send.parentIndex]
                    : null;
            const parentSplitType =
                parentSend && parentSend.channel === CampaignChannels.EMAIL
                    ? (parentSend as EmailSendInputState).splitType
                    : undefined;
            const isFirstSendOfFlow = parentSplitType !== splitType;

            // Include splitAction and splitType only if it's the first send of the flow
            if (isFirstSendOfFlow) {
                sendForApi = {
                    ...sendForApi,
                    splitAction,
                    splitType,
                };
            }
        }

        return {
            ...sendForApi,
            parentIndex: send.parentIndex,
            parentAudienceDelayInterval: send.parentAudienceDelayInterval,
        };
    };

    const getDirectMailSendForApi = (send: DirectMailSendInputState, index: number): DirectMailSendInput => {
        const { content, ...restOfSend } = send;

        return {
            ...restOfSend,
            contentId: content.id,
            name: send.name,
            segmentId: send.segmentId || null, // Always include segmentId, even if null
        };
    };

    const getSMSSendForApi = (send: SmsSendInputState, index: number): SmsSendInput => {
        const { content, ...restOfSend } = send;

        return {
            ...restOfSend,
            smsContentId: content.id,
            name: send.name,
            segmentId: send.segmentId ?? null,
            cohortId: send.cohortId ?? null,
            smsSenderProfileId: send.smsSenderProfileId ?? null,
        };
    };

    const getVoiceSendForApi = (send: VoiceCallSendInput, index: number): VoiceCallSendInput => ({
        ...send,
        name: send.name,
        segmentId: send.segmentId || null, // Always include segmentId, even if null
    });

    // Helper function to get the appropriate initial state based on the primary channel
    const getInitialStateForChannel = (campaignState: CampaignStateTypes): CampaignStateTypes => {
        // Determine the primary channel from the first send or default to EMAIL
        const primaryChannel = campaignState.sends?.[0]?.channel || CampaignChannels.EMAIL;

        switch (primaryChannel) {
            case CampaignChannels.DIRECT_MAIL:
                return directMailInitialState;
            case CampaignChannels.SMS:
                return smsInitialState;
            case CampaignChannels.VOICE_CALL:
                return voiceCallInitialState;
            case CampaignChannels.EMAIL:
            default:
                return emailInitialState;
        }
    };

    const onContinueClick = () => {
        // Track events when user confirms their selection by clicking Continue
        const activeSend = campaignState.sends[activeSendIndex];
        // Track Audience Selected
        if (activeStep === CustomCampaignSteps.SELECT_AUDIENCE) {
            const firstSend = campaignState.sends[0];
            if (firstSend.cohortId) {
                trackCuredEvent(
                    MixpanelEvent.AUDIENCE_SELECTED,
                    {
                        campaign_type: firstSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        audience_type: AudienceType.COHORT,
                        audience_id: firstSend.cohortId,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            } else if (firstSend.segmentId) {
                trackCuredEvent(
                    MixpanelEvent.AUDIENCE_SELECTED,
                    {
                        campaign_type: firstSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        audience_type: AudienceType.AUDIENCE,
                        audience_id: firstSend.segmentId,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            }
        }

        // Track Content Added
        if (activeStep === CustomCampaignSteps.SELECT_CONTENT) {
            // Extract content ID and name based on channel type
            let contentId: number | null = null;
            let contentName: string = 'unknown';

            if (activeSend.channel === CampaignChannels.EMAIL) {
                const emailSend = activeSend as EmailSendInputState;
                contentId = emailSend.templateId;
                // Email sends don't store template name in state
            } else {
                // SMS, Direct Mail, and Voice Call all use the content object pattern
                const sendWithContent = activeSend as SmsSendInputState | DirectMailSendInputState | VoiceCallSendInput;
                if (sendWithContent.content) {
                    contentId = sendWithContent.content.id;
                    contentName = sendWithContent.content.name || 'unknown';
                }
            }

            if (contentId) {
                trackCuredEvent(
                    MixpanelEvent.CONTENT_ADDED,
                    {
                        campaign_type: activeSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        content_id: contentId,
                        content_name: contentName,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            }
        }

        // Track Schedule Set
        if (activeStep === CustomCampaignSteps.SCHEDULE_CURATION) {
            if (activeSend.scheduledTimestamp) {
                trackCuredEvent(
                    MixpanelEvent.SCHEDULE_SET,
                    {
                        campaign_type: activeSend.channel,
                        flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                        scheduled_timestamp: activeSend.scheduledTimestamp,
                    },
                    MixpanelFlow.CAMPAIGN
                );
            }
        }

        // Check if there's a custom continue handler first
        if (customContinueHandler) {
            customContinueHandler();
            return;
        }

        // Default continue logic
        const isActiveSendEmail = activeSend?.channel === CampaignChannels.EMAIL;

        const effectiveSplitType = getEffectiveSplitType(activeSend as any, campaignState.sends);

        if (
            hasMultipleSendsConditionalSplitFlag &&
            activeStep === CustomCampaignSteps.SELECT_CONTENT &&
            isActiveSendEmail &&
            !!effectiveSplitType
        ) {
            const nextSend = campaignState.sends[activeSendIndex + 1] as any;
            const isLastInBranch = !nextSend || nextSend.parentIndex !== activeSendIndex;

            if (isLastInBranch) {
                setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP_CONDITIONAL_SPLIT);
            } else {
                setActiveSendIndex(activeSendIndex + 1);
                setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
            }
            return;
        }

        // A/B Testing Content Flow: increment activeSendIndex when going from Email-A to Email-B
        const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
        const isEmailA = isABTestingContent && activeSendIndex === 0;

        if (
            activeStep === CustomCampaignSteps.SELECT_CONTENT &&
            isActiveSendEmail &&
            (activeSend?.splitType === SplitTypes.MEETS || isEmailA)
        ) {
            setActiveSendIndex(activeSendIndex + 1);
        }
        setActiveStep(nextStep);
    };

    const getSendsForApi = () =>
        campaignState.sends.map((send, index) => {
            let processedSend;
            if (send.channel === CampaignChannels.EMAIL) {
                processedSend = getEmailSendForApi(send as EmailSendInputState, index, campaignState);
            } else if (send.channel === CampaignChannels.DIRECT_MAIL) {
                processedSend = getDirectMailSendForApi(send as DirectMailSendInputState, index);
            } else if (send.channel === CampaignChannels.SMS) {
                processedSend = getSMSSendForApi(send as SmsSendInputState, index);
            } else if (send.channel === CampaignChannels.VOICE_CALL) {
                processedSend = getVoiceSendForApi(send as VoiceCallSendInput, index);
            } else {
                processedSend = send;
            }

            // Sanitize NOOP sends when editing paused curations
            if (isEditingPausedCuration) {
                return sanitizeSendForPausedEdit(processedSend);
            }
            return processedSend;
        });

    const onPublishClick = () => {
        const campaignStateForAPI = {
            ...campaignState,
            sends: getSendsForApi(),
        };

        mutate(campaignStateForAPI);
        recordSatismeterEvent('publish_curation');
    };

    const onPublishAndResumeClick = () => {
        const campaignStateForAPI = {
            ...campaignState,
            sends: getSendsForApi(),
        };

        mutatePublishAndResume(campaignStateForAPI);
        recordSatismeterEvent('publish_curation');
    };

    const onSaveDraftClick = () => {
        const sendsForApi = (campaignState.sends || []).map((send, index) => {
            if (send.channel === CampaignChannels.EMAIL) {
                return getEmailSendForApi(send as EmailSendInputState, index, campaignState);
            }
            if (send.channel === CampaignChannels.DIRECT_MAIL) {
                return getDirectMailSendForApi(send as DirectMailSendInputState, index);
            }
            if (send.channel === CampaignChannels.SMS) {
                return getSMSSendForApi(send as SmsSendInputState, index);
            }
            if (send.channel === CampaignChannels.VOICE_CALL) {
                return getVoiceSendForApi(send as VoiceCallSendInput, index);
            }
            return send;
        });

        const appropriateInitialState = getInitialStateForChannel(campaignState);
        const campaignStateForAPI = {
            ...appropriateInitialState,
            ...campaignState,
            sends: sendsForApi,
        };

        setIsSaveDraftClicked(true);
        if (campaignId) {
            mutateUpdateDraft(campaignStateForAPI);
        } else {
            mutateCreateDraft(campaignStateForAPI);
        }
    };

    const onRemoveClick = (index: number) => {
        if (index === activeSendIndex) {
            setActiveSendIndex(activeSendIndex - 1);
            setActiveStep(CustomCampaignSteps.CHOOSE_NEXT_STEP);
        }

        dispatchCampaignAction(removeSend());
    };

    const handleExitCurationWorkflow = (path: string) => {
        history.push(path);
        recordSatismeterEvent('publish_curation_popup');
    };

    const getIsNavigationBlocked = (location: Location): boolean =>
        !location.pathname.includes(URLPaths.CURATIONS_CUSTOM) && !isSuccessDialogOpen;

    const navigationSx = {
        marginLeft: '5rem',
    };

    return (
        <Flow
            feedback={
                <>
                    {isContentUnavailable && (
                        <ContentUnavailableAlert
                            contentId={unavailableContentId}
                            channel={campaignState.sends[activeSendIndex]?.channel}
                        />
                    )}
                    {isCreateCampaignError && !isCreateCampaignLoading && (
                        <ErrorSnackbar errorMessage={createCampaignError.message} />
                    )}
                    {isPublishAndResumeError && !isPublishAndResumeLoading && (
                        <ErrorSnackbar errorMessage={publishAndResumeError.message} />
                    )}
                    {isCreateDraftCampaignError && !isCreateDraftCampaignLoading && (
                        <ErrorSnackbar errorMessage={createDraftCampaignError.message} />
                    )}
                    {isUpdateDraftCampaignError && !isUpdateDraftCampaignLoading && (
                        <ErrorSnackbar errorMessage={updateDraftCampaignError.message} />
                    )}
                    <RouteLeavingGuard
                        dialogProps={
                            hasCurationsDraft &&
                            status === CampaignStatus.DRAFT &&
                            activeStep !== CustomCampaignSteps.NAME_CAMPAIGN
                                ? draftCampaignDialogProps
                                : campaignDialogProps
                        }
                        shouldBlockNavigation={getIsNavigationBlocked}
                        onAbandon={(destination: string) => {
                            trackCuredEvent(
                                MixpanelEvent.CAMPAIGN_ABANDONED,
                                {
                                    campaign_type: campaignState.sends?.[0]?.channel || 'unknown',
                                    flow_type: CampaignFlowType.CUSTOM_CAMPAIGN,
                                    destination,
                                },
                                MixpanelFlow.CAMPAIGN
                            );
                        }}
                        {...(hasCurationsDraft &&
                            status === CampaignStatus.DRAFT && {
                                // Save draft but don't navigate - let RouteLeavingGuard use lastLocation
                                // to navigate to the originally intended destination
                                onConfirm: () => {
                                    onSaveDraftClick();
                                },
                                // Don't navigate - let RouteLeavingGuard use lastLocation
                                onCancel: () => {},
                            })}
                    />
                    <SuccessDialog
                        title={isSaveDraftClicked ? 'Draft Saved' : 'high five!'}
                        body={isSaveDraftClicked ? DraftSuccessBody : PublishSuccessBody}
                        isOpen={isSuccessDialogOpen}
                        setIsOpen={setIsSuccessDialogOpen}
                        onClose={() => {
                            setIsSuccessDialogOpen(false);
                            setIsSaveDraftClicked(false); // Reset draft state when dialog is closed
                            setSavedDraftCampaignId(null); // Reset saved campaign ID
                        }}
                        buttonOneText={isSaveDraftClicked ? 'Continue' : 'Insights'}
                        buttonTwoText={isSaveDraftClicked ? 'Exit' : 'Curations'}
                        buttonOneOnClick={
                            isSaveDraftClicked
                                ? () => {
                                      setIsSuccessDialogOpen(false);
                                      setIsSaveDraftClicked(false); // Reset draft state
                                      // Navigate to the saved draft campaign for editing
                                      if (savedDraftCampaignId) {
                                          const targetCampaignId = savedDraftCampaignId;
                                          setSavedDraftCampaignId(null); // Reset saved campaign ID
                                          history.replace(`${URLPaths.CURATIONS_CUSTOM}/${targetCampaignId}`);
                                      }
                                  }
                                : () => handleExitCurationWorkflow(URLPaths.INSIGHTS)
                        }
                        buttonTwoOnClick={
                            isSaveDraftClicked
                                ? () => handleExitCurationWorkflow(URLPaths.CURATIONS)
                                : () => handleExitCurationWorkflow(URLPaths.CURATIONS)
                        }
                    />
                </>
            }
            topLeft={
                activeStep === CustomCampaignSteps.NAME_CAMPAIGN ? null : (
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
                        <CustomCampaignNameInput
                            name={campaignState.name}
                            handleNameChange={(name: string) => dispatchCampaignAction(setName(name))}
                            withClickAway
                            disabled={disableNameEditing}
                        />
                        {status && (
                            <Box sx={{ pt: 3 }}>
                                <CampaignStatusText status={status} />
                            </Box>
                        )}
                    </Box>
                )
            }
            topRight={
                <CustomCampaignNavigationButtons
                    activeStep={activeStep}
                    isContinueEnabled={isContinueEnabled}
                    onContinueClick={onContinueClick}
                    onPublishClick={onPublishClick}
                    onPublishAndResumeClick={onPublishAndResumeClick}
                    onSaveDraftClick={onSaveDraftClick}
                    setIsContinueEnabled={setIsContinueEnabled}
                />
            }
            bottomLeft={
                <CustomCampaignForm
                    activeSendIndex={activeSendIndex}
                    activeStep={activeStep}
                    campaignState={campaignState}
                    contentPreviewId={contentPreviewId}
                    customContinueHandler={customContinueHandler}
                    dispatchCampaignAction={dispatchCampaignAction}
                    initialContentSubStep={initialContentSubStep}
                    navigateToABTestScheduling={navigateToABTestScheduling}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                    setContentPreviewId={setContentPreviewId}
                    setCustomContinueHandler={setCustomContinueHandlerFixed}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                    onContentUnavailableChange={handleContentUnavailableChange}
                />
            }
            bottomRight={
                contentPreviewId ? (
                    <CustomCampaignContentPreview
                        activeSendIndex={activeSendIndex}
                        campaignState={campaignState}
                        contentPreviewId={contentPreviewId}
                        setContentPreviewId={setContentPreviewId}
                    />
                ) : (
                    <div style={navigationSx}>
                        {hasCampaignGraphNavigationFlag ? (
                            <GraphCampaignNavigation
                                activeSendIndex={activeSendIndex}
                                activeStep={activeStep}
                                campaignState={campaignState}
                                onRemoveClick={onRemoveClick}
                                setActiveSendIndex={setActiveSendIndex}
                                setActiveStep={setActiveStep}
                            />
                        ) : (
                            <CustomCampaignNavigation
                                activeSendIndex={activeSendIndex}
                                activeStep={activeStep}
                                campaignState={campaignState}
                                onRemoveClick={onRemoveClick}
                                setActiveSendIndex={setActiveSendIndex}
                                setActiveStep={setActiveStep}
                            />
                        )}
                    </div>
                )
            }
        />
    );
};

export default CustomCampaign;
